import type { SnippetDraft } from '../drafts'
import type { SnippetImpact, SnippetOrgUsage, SnippetVariant } from '../types'
import { otherOrgs, plural, relativeUpdatedAt } from '../utils'

type SheetKind =
  | 'new'
  | 'private'
  | 'sharedAdmin'
  | 'sharedReader'
  | 'careforce'
  | 'customize'
  | 'copy'
  | 'variant'

/**
 * Precedence mirrors how drafts are built (drafts.ts, SnippetEditorSheetHost).
 * Read from the live draft: flipping "Share with all orgs" moves a row between
 * private and sharedAdmin, which adds or removes the Organizations tab.
 */
function sheetKind(draft: SnippetDraft): SheetKind {
  if (draft.variant) return 'variant'
  if (draft.readOnly) return 'careforce'
  if (draft.customize) return 'customize'
  if (draft.copy) return 'copy'
  if (draft.id === null) return 'new'
  if (!draft.isShared) return 'private'
  return draft.canShare ? 'sharedAdmin' : 'sharedReader'
}

export type TabKey = 'content' | 'variants' | 'orgs'
export type SharingState = 'private' | 'shared' | 'sharedHidden'
export type BadgeId =
  | 'draft'
  | 'shared'
  | 'hidden'
  | 'careforce'
  | 'yourCopy'
  | 'outOfDate'
  | 'variant'
export type CalloutId =
  | 'customize'
  | 'outOfDate'
  | 'managedElsewhere'
  | 'variantFork'
/** Where the Content tab's Compare reads its old side from. */
export type CompareBase = 'source' | 'opened' | 'variantShared'

export interface CompareSpec {
  base: CompareBase
  toggle: string
  from: string
  to: string
  useBase: string
  /** Toast after "use base": the draft now holds the base text. */
  used: string
}

export type FooterAside = 'resetVariant' | 'resetToCareforce' | 'careforceNote'
export interface FooterSpec {
  aside: FooterAside | null
  dismiss: 'Cancel' | 'Close'
  /** Renders only when the sheet holds a handler for `action`. */
  primary: { action: 'save' | 'customize'; label: string } | null
}

/** Resolved once per render, so every reader counts the same rows. */
interface Counts {
  duties: number
  variants: number
  orgs: number
  /** The duty a variant draft edits, when the variants list names it. */
  dutyName: string | null
}
interface In {
  draft: SnippetDraft
  counts: Counts
}

interface KindSpec {
  title: (i: In) => string
  description: string
  /** The name is the snippet's, so a variant or a Careforce row cannot rename it. */
  nameEditable: boolean
  editor: 'edit' | 'preview'
  tabs: readonly TabKey[]
  badges: (i: In) => BadgeId[]
  facts: (i: In) => string[]
  sharing: (i: In) => SharingState | null
  callout: (i: In) => CalloutId | null
  compare: CompareSpec | null
  /** An own snippet: editing its body changes the duties that use it. */
  impact: boolean
  footer: (i: In) => FooterSpec
}

const none = () => null
const hidden = (draft: SnippetDraft): BadgeId[] =>
  draft.isHidden ? ['hidden'] : []
const ago = (verb: string, iso: string | null | undefined) =>
  iso ? [`${verb} ${relativeUpdatedAt(iso)}`] : []
const ownFacts = ({ draft, counts }: In, withOrgs: boolean) => [
  plural(counts.duties, 'duty'),
  ...(withOrgs ? [plural(counts.orgs, 'org')] : []),
  ...(counts.variants > 0 ? [plural(counts.variants, 'variant')] : []),
  ...ago('Edited', draft.updatedAt),
]

const CAREFORCE_COMPARE = {
  toggle: 'Compare with Careforce',
  from: 'Careforce',
  to: 'your draft',
  useBase: 'Use Careforce version',
  used: 'Draft now matches Careforce. Save to apply.',
} as const

const saveAs = (
  label: string,
  aside: FooterAside | null = null
): FooterSpec => ({
  aside,
  dismiss: 'Cancel',
  primary: { action: 'save', label },
})

const OWN = {
  title: () => 'Edit snippet',
  description: 'Edit the primary snippet',
  nameEditable: true,
  editor: 'edit',
  tabs: ['content', 'variants'],
  badges: () => [],
  facts: (i: In) => ownFacts(i, false),
  sharing: none,
  callout: none,
  compare: null,
  impact: true,
  footer: () => saveAs('Save'),
} as const

const KIND_SPECS = {
  new: {
    ...OWN,
    title: () => 'New snippet',
    tabs: ['content'],
    badges: () => ['draft'],
    facts: () => ['Not used by any duty yet'],
    impact: false,
    footer: () => saveAs('Create snippet'),
  },
  private: {
    ...OWN,
    sharing: ({ draft }) => (draft.canShare ? 'private' : null),
  },
  sharedAdmin: {
    ...OWN,
    tabs: ['content', 'variants', 'orgs'],
    // The sharing chip carries Shared and Hidden.
    facts: (i) => ownFacts(i, true),
    sharing: ({ draft }) => (draft.isHidden ? 'sharedHidden' : 'shared'),
  },
  sharedReader: {
    ...OWN,
    badges: ({ draft }) => ['shared', ...hidden(draft)],
  },
  careforce: {
    title: ({ draft }) => draft.name,
    description: 'A snippet managed by Careforce',
    nameEditable: false,
    editor: 'preview',
    tabs: ['content'],
    badges: ({ draft }) => ['careforce', ...hidden(draft)],
    facts: ({ draft, counts }) => [
      'Managed by Careforce',
      counts.duties === 0
        ? 'Not used by your duties yet'
        : `Used in ${counts.duties} of your duties`,
      ...ago('Updated', draft.updatedAt),
    ],
    sharing: none,
    callout: ({ draft }) =>
      draft.managedElsewhere ? 'managedElsewhere' : null,
    compare: null,
    impact: false,
    footer: () => ({
      aside: 'careforceNote',
      dismiss: 'Close',
      primary: {
        action: 'customize',
        label: 'Customize for your organization',
      },
    }),
  },
  customize: {
    ...OWN,
    title: () => 'Customize snippet',
    description: 'Create your own copy of a Careforce snippet',
    tabs: ['content'],
    badges: () => ['yourCopy'],
    facts: ({ counts }) => [
      'Customizing a Careforce snippet',
      plural(counts.duties, 'duty'),
    ],
    callout: () => 'customize',
    compare: { base: 'opened', ...CAREFORCE_COMPARE },
    impact: false,
    footer: () => saveAs('Save as your copy'),
  },
  copy: {
    ...OWN,
    badges: ({ draft }) =>
      draft.copy?.outOfDate ? ['yourCopy', 'outOfDate'] : ['yourCopy'],
    facts: ({ draft, counts }) => [
      'From Careforce',
      plural(counts.duties, 'duty'),
      ...ago('Edited', draft.updatedAt),
    ],
    callout: ({ draft }) => (draft.copy?.outOfDate ? 'outOfDate' : null),
    compare: { base: 'source', ...CAREFORCE_COMPARE },
    impact: false,
    footer: () => saveAs('Save', 'resetToCareforce'),
  },
  variant: {
    ...OWN,
    title: () => 'Edit the variant for this duty',
    description: "Edit this duty's variant of the snippet",
    nameEditable: false,
    tabs: ['content', 'variants'],
    badges: () => ['variant'],
    facts: ({ draft, counts }) => [
      `Only in ${counts.dutyName ?? 'this duty'}`,
      `Shared text used by ${plural(
        Math.max(
          0,
          counts.duties - counts.variants - (draft.variant?.updatedAt ? 0 : 1)
        ),
        'other duty'
      )}`,
      ...(draft.variant?.updatedAt
        ? ago('Updated', draft.variant.updatedAt)
        : ['Not saved yet']),
    ],
    // Until a row exists, saving is what forks this duty off the shared text.
    callout: ({ draft }) => (draft.variant?.updatedAt ? null : 'variantFork'),
    compare: {
      base: 'variantShared',
      toggle: 'Compare with shared',
      from: 'Shared',
      to: 'this variant',
      useBase: 'Use shared text',
      used: 'Draft now matches the shared text. Save to apply.',
    },
    impact: false,
    // Reset only once a row exists to reset.
    footer: ({ draft }) =>
      saveAs('Save variant', draft.variant?.updatedAt ? 'resetVariant' : null),
  },
} satisfies Record<SheetKind, KindSpec>

const TAB_LABELS: Record<TabKey, string> = {
  content: 'Content',
  variants: 'Variants',
  orgs: 'Organizations',
}

/** Everything the sheet renders for a draft, resolved. */
export interface SheetModel {
  kind: SheetKind
  title: string
  description: string
  nameEditable: boolean
  editor: 'edit' | 'preview'
  badges: BadgeId[]
  facts: string[]
  /** ID and Token, copy on click. None until the row exists. */
  chips: { label: 'ID' | 'Token'; value: string; copyValue: string }[]
  tabs: { value: TabKey; label: string; count?: number }[]
  sharing: SharingState | null
  callout: CalloutId | null
  compare: CompareSpec | null
  /** Non-null exactly when the impact strip shows: who a save reaches. */
  impact: { reach: string } | null
  footer: FooterSpec
}

export function tabsOf(draft: SnippetDraft | null): readonly TabKey[] {
  return draft ? KIND_SPECS[sheetKind(draft)].tabs : ['content']
}

export function compareOf(draft: SnippetDraft | null): CompareSpec | null {
  return draft && KIND_SPECS[sheetKind(draft)].compare
}

export function sheetModel(
  draft: SnippetDraft,
  data: {
    variants: SnippetVariant[] | null
    usage: SnippetOrgUsage[] | null
  }
): SheetModel {
  const kind = sheetKind(draft)
  const spec: KindSpec = KIND_SPECS[kind]
  const counts: Counts = {
    // A shared row's reach spans every org; the rest count this org's duties.
    duties:
      draft.isShared && draft.reach
        ? draft.reach.dutyCount
        : draft.instanceCount,
    variants: data.variants?.length ?? draft.variantCount ?? 0,
    orgs: data.usage
      ? otherOrgs(data.usage).length
      : (draft.reach?.otherOrgCount ?? 0),
    dutyName:
      data.variants?.find((row) => row.duty_id === draft.variant?.dutyId)
        ?.duty_name ?? null,
  }
  const input = { draft, counts }
  const ref = draft.referenceId ?? draft.id
  return {
    kind,
    title: spec.title(input),
    description: spec.description,
    nameEditable: spec.nameEditable,
    editor: spec.editor,
    badges: spec.badges(input),
    facts: spec.facts(input),
    chips: ref ? tokenChips(ref) : [],
    tabs: spec.tabs.map((value) => ({
      value,
      label: TAB_LABELS[value],
      count:
        value === 'variants'
          ? counts.variants
          : value === 'orgs'
            ? counts.orgs
            : undefined,
    })),
    sharing: spec.sharing(input),
    callout: spec.callout(input),
    compare: spec.compare,
    impact: spec.impact ? impactOf(draft, counts) : null,
    footer: spec.footer(input),
  }
}

/**
 * An own snippet that duties use, with unsaved CONTENT changes: a rename or a
 * share toggle reaches no duty's text.
 */
function impactOf(draft: SnippetDraft, counts: Counts): SheetModel['impact'] {
  if (draft.savedContent === undefined) return null
  if (draft.content === draft.savedContent) return null
  if (counts.duties === 0) return null
  if (!draft.isShared) return { reach: plural(counts.duties, 'duty') }
  const { reach } = draft
  // Duties a save updates; without the relay's count, all less variants.
  const updated =
    reach?.updatedDutyCount ?? Math.max(0, counts.duties - counts.variants)
  if (reach?.readingOrgCount == null) return { reach: plural(updated, 'duty') }
  return {
    reach: `${plural(updated, 'duty')} in ${plural(reach.readingOrgCount, 'org')}`,
  }
}

function tokenChips(ref: string): SheetModel['chips'] {
  const token = `{snippet:${ref}}`
  const short = ref.length > 8 ? `{snippet:${ref.slice(0, 8)}…}` : token
  return [
    { label: 'ID', value: ref, copyValue: ref },
    { label: 'Token', value: short, copyValue: token },
  ]
}

/** The impact strip's state; `sheetModel().impact` decides whether it shows. */
export type ImpactStrip =
  | { state: 'idle' | 'checking' | 'ok' | 'stale' }
  | { state: 'bad'; impact: SnippetImpact }
  | { state: 'error'; message: string }

export function impactStripFor({
  impact,
  isChecking,
  isStale,
  error,
}: {
  impact: SnippetImpact | null
  isChecking: boolean
  /** An edit outdated the last result. */
  isStale: boolean
  error: string | null
}): ImpactStrip {
  if (isChecking) return { state: 'checking' }
  if (error) return { state: 'error', message: error }
  if (isStale) return { state: 'stale' }
  if (!impact) return { state: 'idle' }
  return impact.duties.some((duty) => !duty.ok)
    ? { state: 'bad', impact }
    : { state: 'ok' }
}
